import { useState, useEffect, useRef } from 'react';

export default function AthleteSearchInput({ value, onChange, availableAthletes, placeholder = 'Select athlete...' }) {
  const [inputValue, setInputValue] = useState(value || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const filteredAthletes = inputValue
    ? availableAthletes.filter((name) => name.toLowerCase().includes(inputValue.toLowerCase()))
    : availableAthletes;

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setShowSuggestions(true);
    setHighlightedIndex(-1);

    const exactMatch = availableAthletes.find(
      (name) => name.toLowerCase() === newValue.toLowerCase()
    );
    if (exactMatch) {
      onChange(exactMatch);
    } else if (newValue === '') {
      onChange('');
    }
  };

  const handleSelect = (name) => {
    setInputValue(name);
    onChange(name);
    setShowSuggestions(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || filteredAthletes.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < Math.min(filteredAthletes.length, 15) - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelect(filteredAthletes[highlightedIndex]);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const toggleDropdown = () => {
    setShowSuggestions(!showSuggestions);
    if (!showSuggestions) {
      inputRef.current?.focus();
    }
  };

  return (
    <div className="relative flex-1">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setShowSuggestions(true)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="border p-2 pr-8 rounded w-full text-blue-900 bg-white"
          autoComplete="off"
        />
        <button
          type="button"
          onClick={toggleDropdown}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          tabIndex={-1}
        >
          <svg className={`w-4 h-4 transition-transform ${showSuggestions ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {showSuggestions && filteredAthletes.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {filteredAthletes.slice(0, 15).map((name, idx) => (
            <button
              key={name}
              type="button"
              onClick={() => handleSelect(name)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-theme-bg-secondary ${
                idx === highlightedIndex ? 'bg-theme-bg-secondary' : ''
              } ${name === value ? 'font-semibold text-theme-primary' : 'text-gray-700'}`}
            >
              {name}
            </button>
          ))}
          {filteredAthletes.length > 15 && (
            <div className="px-3 py-2 text-xs text-gray-500 border-t">
              Type to filter... ({filteredAthletes.length - 15} more)
            </div>
          )}
        </div>
      )}

      {showSuggestions && inputValue && filteredAthletes.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3 text-sm text-gray-500">
          No athletes found matching "{inputValue}"
        </div>
      )}
    </div>
  );
}
